// The Atelier (SPEC §11 and §18, admins only): a full-screen workspace to change the application
// by chatting with Claude Code. Left: the sessions. Centre: the conversation (streamed live from
// SSE 'atelier' events). Right: the changes (diff, tests, APPLIQUER ET DÉPLOYER, ABANDONNER).
// On phones the three columns become three views switched from the top.
// Endpoints: GET /api/atelier/status · GET|POST /api/atelier/sessions · GET …/sessions/:id ·
// POST …/:id/messages · GET …/:id/diff · POST …/:id/test · POST …/:id/apply {force?, message?} ·
// POST …/:id/discard. Every action is checked (admin) and audited by the server.
import { h, button, iconButton, badge, segmented, emptyState, spinner, icon, input, field, switchToggle } from '../dom.js';
import { t } from '../i18n/fr.js';
import { isConflict } from '../api.js';
import { relativeTime, formatDateTime } from '../format.js';
import {
  stateInfo, sendBlocker, sessionActions, applyEvent, applyEventToList, upsertSession, normalizeSessions,
  formatStat, canStart, INTRO_KEY,
} from './atelier/model.js';
import { parseUnifiedDiff } from './atelier/diff.js';
import { createConversation } from './atelier/conversation.js';
import { createChangesPanel } from './atelier/changes.js';
import './atelier/strings.js';

const enc = encodeURIComponent;
/** Reload of the open session after a state event (collects the steps the server did not stream). */
const RELOAD_DELAY_MS = 250;
const DIFF_DELAY_MS = 600;
/** Without a live SSE connection, a running session is polled this often. */
const POLL_MS = 5000;
const MOBILE_VIEWS = ['sessions', 'chat', 'changes'];

function storageGet(key) {
  try { return globalThis.localStorage?.getItem(key) ?? null; } catch { return null; }
}

function storageSet(key, value) {
  try { globalThis.localStorage?.setItem(key, value); } catch { /* private mode, blocked storage */ }
}

/**
 * Opens the Atelier (route #/atelier; the router only calls it for admins when features.atelier).
 * @param {object} ctx app context
 * @returns {import('./overlay.js').Layer}
 */
export function openAtelier(ctx) {
  let status = null;
  let statusMissing = false;
  let sessions = null;
  let listError = null;
  let currentId = null;      // null: the "new session" screen
  let current = null;        // the full open session
  let currentError = null;
  let diff = null;
  let view = 'chat';
  let closed = false;
  let seq = 0;
  let diffSeq = 0;
  let reloadTimer = null;
  let diffTimer = null;
  const drafts = new Map();
  let introOpen = storageGet(INTRO_KEY) !== '1';

  // --- layout -----------------------------------------------------------------------------------
  const banners = h('div', { class: 'atl-banners' });
  const intro = h('section', { class: 'atl-intro', 'aria-labelledby': 'atl-intro-title' });
  const listEl = h('ul', { class: 'atl-session-list', 'aria-label': t('atl.sessions.title') });
  const newBtn = button(t('atl.sessions.new'), { variant: 'primary', size: 'sm', icon: 'plus', onClick: () => select(null, { focus: true }) });
  const sessionsCol = h('aside', { class: 'atl-sessions', 'aria-label': t('atl.sessions.title') },
    h('header', { class: 'atl-sessions-head' }, h('h3', { class: 'atl-sessions-title mono-label' }, t('atl.sessions.title')), newBtn),
    listEl);
  const conversation = createConversation(ctx, { onSend: send });
  const changes = createChangesPanel(ctx, {
    onTest: runTests, onApply: openApply, onDiscard: discard, onRefresh: () => { loadDiff(); loadCurrent(); },
  });
  const switcher = segmented(MOBILE_VIEWS.map((v) => ({ value: v, label: t(`atl.view.${v}`) })), {
    value: view, size: 'sm', label: t('atl.views'), onChange: (v) => setView(v),
  });
  switcher.classList.add('atl-switcher');
  const grid = h('div', { class: 'atl-grid', dataset: { view } }, sessionsCol, conversation.el, changes.el);
  const root = h('div', { class: 'atl' }, h('div', { class: 'atl-top' }, switcher), banners, intro, grid);

  const helpBtn = iconButton('info', t('atl.help'), { onClick: () => { introOpen = !introOpen; renderIntro(); } });
  const layer = ctx.overlay.openSheet({
    title: t('atelier.title'),
    eyebrow: t('atl.eyebrow'),
    subtitle: t('atl.subtitle'),
    tone: 'dark',
    className: 'atl-sheet',
    actions: [helpBtn],
    content: root,
  });

  function setView(v) {
    view = MOBILE_VIEWS.includes(v) ? v : 'chat';
    grid.dataset.view = view;
    switcher.setValue(view);
  }

  // --- rendering --------------------------------------------------------------------------------
  function renderIntro() {
    intro.hidden = !introOpen;
    helpBtn.setAttribute('aria-expanded', String(introOpen));
    if (!introOpen) { intro.replaceChildren(); return; }
    intro.replaceChildren(
      h('div', { class: 'atl-intro-icon', 'aria-hidden': 'true' }, icon('hammer', { size: 20 })),
      h('div', { class: 'atl-intro-text' },
        h('h3', { class: 'atl-intro-title', id: 'atl-intro-title' }, t('atl.intro.title')),
        h('ol', { class: 'atl-intro-list' }, ['1', '2', '3', '4'].map((n) => h('li', {}, t(`atl.intro.${n}`))))),
      button(t('atl.intro.ok'), {
        variant: 'secondary', size: 'sm', class: 'atl-intro-ok',
        onClick: () => { introOpen = false; storageSet(INTRO_KEY, '1'); renderIntro(); conversation.focus(); },
      }));
  }

  function renderBanners() {
    const items = [];
    const alert = (tone, text, detail) => h('div', { class: ['atl-alert', `is-${tone}`], role: tone === 'danger' ? 'alert' : 'note' },
      icon(tone === 'info' ? 'info' : 'alert', { size: 16 }),
      h('div', {}, h('p', { class: 'atl-alert-text' }, text), detail ? h('p', { class: 'atl-alert-detail' }, detail) : null));
    if (status?.enabled === false) items.push(alert('danger', t('atl.unavailable.disabled')));
    else if (status?.repo && status.repo.ok === false) {
      // The server's own sentence already says what to do; the generic hint is only a fallback.
      const detail = status.repo.error ? `${status.repo.error} ${t('atl.unavailable.readOnly')}` : t('atl.unavailable.repoHint');
      items.push(alert('danger', t('atl.unavailable.repo'), detail));
    }
    if (status?.mock) items.push(alert('info', t('atl.mock')));
    banners.replaceChildren(...items);
    banners.hidden = !items.length;
    const sub = status?.branch ? t('atl.subtitleRepo', { branch: status.branch, model: status.model || '—' }) : t('atl.subtitle');
    layer.setSubtitle(sub);
  }

  function renderSessions() {
    const focusedId = listEl.contains?.(globalThis.document?.activeElement) ? globalThis.document.activeElement.dataset?.id : null;
    newBtn.disabled = !canStart(status);
    const items = [];
    items.push(h('li', {}, h('button', {
      type: 'button', class: ['atl-session', 'is-new', { 'is-active': currentId === null }], dataset: { id: '' },
      'aria-current': currentId === null ? 'true' : null, onClick: () => select(null, { focus: true }),
    }, h('span', { class: 'atl-session-title' }, icon('plus', { size: 14 }), t('atl.sessions.newItem')),
    h('span', { class: 'atl-session-meta muted' }, t('atl.sessions.newHint')))));
    if (sessions === null && !listError) {
      items.push(h('li', { class: 'atl-session-loading' }, spinner(t('atl.loading'))));
    } else if (listError && !sessions) {
      items.push(h('li', { class: 'atl-session-error' }, h('p', { class: 'muted' }, listError.message || t('atl.sessions.error')),
        button(t('atl.retry'), { variant: 'link', size: 'sm', onClick: () => loadSessions() })));
    } else if (!sessions.length) {
      items.push(h('li', { class: 'atl-session-empty muted' }, t('atl.sessions.empty')));
    } else {
      for (const s of sessions) {
        const info = stateInfo(s);
        const stat = formatStat(s.diffStat);
        items.push(h('li', {}, h('button', {
          type: 'button', class: ['atl-session', { 'is-active': s.id === currentId }], dataset: { id: s.id },
          'aria-current': s.id === currentId ? 'true' : null, onClick: () => select(s.id, { focus: false }),
        },
        h('span', { class: 'atl-session-title' }, s.title || t('atl.chat.newTitle')),
        h('span', { class: 'atl-session-meta' },
          badge(info.label, info.tone, { class: info.live ? 'atl-live-badge' : '' }),
          h('time', { class: 'muted', datetime: String(s.updatedAt || s.createdAt || ''), title: formatDateTime(s.updatedAt || s.createdAt) },
            relativeTime(s.updatedAt || s.createdAt)),
          stat ? h('span', { class: 'atl-session-stat mono-label' }, stat) : null))));
      }
    }
    listEl.replaceChildren(...items);
    if (focusedId !== null && focusedId !== undefined) {
      const target = [...listEl.querySelectorAll('button')].find((b) => (b.dataset.id || '') === focusedId);
      target?.focus?.({ preventScroll: true });
    }
  }

  function blocker() {
    return sendBlocker(currentId === null ? null : current, { status });
  }

  function renderMain({ full = false } = {}) {
    if (full) conversation.show(currentId === null ? null : current, { blocker: blocker() });
    else conversation.sync(currentId === null ? null : current, { blocker: blocker() });
    changes.update({ session: currentId === null ? null : current, status, diff });
  }

  function renderAll(opts) {
    renderBanners();
    renderSessions();
    renderMain(opts);
  }

  // --- data -------------------------------------------------------------------------------------
  async function loadStatus() {
    try {
      status = await ctx.api.get('/api/atelier/status');
      statusMissing = false;
    } catch (err) {
      if (closed) return;
      statusMissing = err.status === 404;
      if (err.status !== 404) ctx.toast.error(err);
    }
    if (!closed) { renderBanners(); renderSessions(); renderMain(); }
  }

  async function loadSessions() {
    const first = sessions === null;
    try {
      const res = await ctx.api.get('/api/atelier/sessions');
      if (closed) return;
      sessions = normalizeSessions(res);
      listError = null;
    } catch (err) {
      if (closed) return;
      listError = err;
    }
    if (listError?.status === 404 && statusMissing) {
      showUnavailable();
      return;
    }
    renderSessions();
    // On opening, come back to the session still in progress (the most recent one not finished),
    // unless the admin already started typing a new request.
    if (first && sessions && currentId === null && !conversation.getDraft().trim()) {
      const open = sessions.find((s) => s.state === 'running' || s.state === 'testing')
        || (sessions[0] && ['ready', 'failed'].includes(sessions[0].state) ? sessions[0] : null);
      if (open) select(open.id);
    }
  }

  function showUnavailable() {
    grid.hidden = true;
    switcher.hidden = true;
    banners.replaceChildren(emptyState({ icon: 'hammer', title: t('atl.unavailable.title'), text: t('atl.unavailable.soon') }));
    banners.hidden = false;
  }

  async function loadCurrent() {
    if (currentId === null) return;
    const id = currentId;
    const my = ++seq;
    try {
      const s = await ctx.api.get(`/api/atelier/sessions/${enc(id)}`);
      if (closed || my !== seq || id !== currentId) return;
      // Live messages that arrived while the request was in flight are kept.
      current = s && typeof s === 'object' && s.id === id ? mergeFull(current, s) : current;
      currentError = null;
      sessions = upsertSession(sessions, current);
    } catch (err) {
      if (closed || my !== seq || id !== currentId) return;
      currentError = err;
      if (!current || current.id !== id) ctx.toast.error(err);
    }
    renderSessions();
    renderMain();
  }

  async function loadDiff() {
    if (currentId === null || !current) { diff = null; renderMain(); return; }
    const id = currentId;
    if (current.state === 'discarded') { diff = null; renderMain(); return; }
    const my = ++diffSeq;
    const prev = diff && diff.forId === id ? diff : null;
    diff = { forId: id, loading: true, data: prev?.data || null, parsed: prev?.parsed || null, error: null };
    renderMain();
    try {
      const data = await ctx.api.get(`/api/atelier/sessions/${enc(id)}/diff`, { timeoutMs: 60_000 });
      if (closed || my !== diffSeq || id !== currentId) return;
      const clean = {
        stat: data?.stat || null,
        files: Array.isArray(data?.files) ? data.files : [],
        patch: typeof data?.patch === 'string' ? data.patch : '',
        truncated: data?.truncated === true,
      };
      diff = { forId: id, loading: false, data: clean, parsed: parseUnifiedDiff(clean.patch), error: null };
    } catch (err) {
      if (closed || my !== diffSeq || id !== currentId) return;
      diff = { forId: id, loading: false, data: prev?.data || null, parsed: prev?.parsed || null, error: err };
    }
    renderMain();
  }

  function scheduleReload() {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(loadCurrent, RELOAD_DELAY_MS);
  }

  function scheduleDiff() {
    clearTimeout(diffTimer);
    diffTimer = setTimeout(loadDiff, DIFF_DELAY_MS);
  }

  /** Opens a session (or the new-session screen with null). */
  function select(id, { focus = false } = {}) {
    drafts.set(currentId ?? '', conversation.getDraft());
    currentId = id;
    current = id === null ? null : (sessions || []).find((s) => s.id === id) || { id, messages: [] };
    if (current && !Array.isArray(current.messages)) current = { ...current, messages: [] };
    currentError = null;
    diff = null;
    seq += 1;
    diffSeq += 1;
    renderSessions();
    renderMain({ full: true });
    conversation.setDraft(drafts.get(id ?? '') || '');
    if (id !== null) {
      loadCurrent().then(() => { if (!closed && id === currentId) loadDiff(); });
      setView('chat');
    } else {
      setView('chat');
    }
    if (focus) conversation.focus();
  }

  // --- actions ----------------------------------------------------------------------------------
  async function send(text) {
    try {
      if (currentId === null) {
        const s = await ctx.api.post('/api/atelier/sessions', { message: text }, { timeoutMs: 120_000 });
        if (closed) return true;
        drafts.delete('');
        sessions = upsertSession(sessions, s);
        if (status) status = { ...status, running: s.id };
        currentId = s.id;
        current = s;
        diff = null;
        seq += 1;
        renderSessions();
        renderMain({ full: true });
        ctx.toast.success(t('atl.chat.started'), { timeout: 2500 });
        conversation.announce(t('atl.chat.working'));
        return true;
      }
      const id = currentId;
      const s = await ctx.api.post(`/api/atelier/sessions/${enc(id)}/messages`, { message: text }, { timeoutMs: 60_000 });
      if (closed) return true;
      drafts.delete(id);
      if (status) status = { ...status, running: s.id };
      if (id === currentId) {
        // Keep the live messages that may have arrived before the response.
        current = mergeFull(current, s);
        sessions = upsertSession(sessions, current);
        renderSessions();
        renderMain();
        conversation.announce(t('atl.chat.working'));
      }
      return true;
    } catch (err) {
      if (!closed) {
        ctx.toast.error(err);
        if (isConflict(err)) { loadStatus(); loadCurrent(); }
      }
      return false;
    }
  }

  /** A fresh full session, keeping live messages the response does not have yet. */
  function mergeFull(prev, next) {
    if (!next || typeof next !== 'object') return prev;
    if (!prev || prev.id !== next.id) return next;
    let out = next;
    const have = new Set((next.messages || []).map((m) => `${m.role}\u0001${m.at}\u0001${m.text}`));
    for (const m of prev.messages || []) {
      if (!have.has(`${m.role}\u0001${m.at}\u0001${m.text}`) && String(m.at) > String(next.updatedAt || '')) {
        out = { ...out, messages: [...out.messages, m] };
      }
    }
    return out;
  }

  async function runTests() {
    if (!current) return;
    const id = current.id;
    try {
      const s = await ctx.api.post(`/api/atelier/sessions/${enc(id)}/test`, {});
      if (closed || id !== currentId) return;
      current = mergeFull(current, s);
      sessions = upsertSession(sessions, current);
      renderSessions();
      renderMain();
      ctx.toast.info(t('atl.action.testStarted'), { timeout: 2500 });
    } catch (err) {
      if (!closed) { ctx.toast.error(err); loadCurrent(); }
    }
  }

  async function discard() {
    if (!current) return;
    const s0 = current;
    const ok = await ctx.overlay.confirm({
      title: t('atl.discard.title'),
      message: s0.state === 'running' ? `${t('atl.discard.text')} ${t('atl.discard.running')}` : t('atl.discard.text'),
      confirmLabel: t('atl.discard.confirm'),
      danger: true,
    });
    if (!ok || closed) return;
    try {
      const s = await ctx.api.post(`/api/atelier/sessions/${enc(s0.id)}/discard`, {}, { timeoutMs: 60_000 });
      if (closed) return;
      sessions = upsertSession(sessions, s);
      if (status?.running === s0.id) status = { ...status, running: null };
      if (s0.id === currentId) { current = s; diff = null; }
      renderAll();
      ctx.toast.success(t('atl.discard.done'));
    } catch (err) {
      if (!closed) { ctx.toast.error(err); loadCurrent(); }
    }
  }

  function openApply() {
    if (!current) return;
    const s0 = current;
    const acts = sessionActions(s0, { mock: !!status?.mock });
    if (!acts.apply) { if (acts.why.apply) ctx.toast.warn(acts.why.apply); return; }
    const tests = s0.tests;
    const testsLine = tests?.ok === true
      ? h('p', { class: 'atl-apply-tests is-ok' }, icon('check', { size: 16 }), h('span', {}, t('atl.apply.testsOk', { when: relativeTime(tests.at) })))
      : h('p', { class: 'atl-apply-tests is-warn' }, icon('alert', { size: 16 }), h('span', {}, tests ? t('atl.apply.testsFailed') : t('atl.apply.testsNone')));
    const msgInput = input({ maxLength: 100, placeholder: s0.title || '', autocomplete: 'off' });
    const force = switchToggle(t('atl.apply.force'), { hint: t('atl.apply.forceHint'), onChange: () => sync() });
    const errBox = h('p', { class: 'atl-alert is-danger', role: 'alert', hidden: true });
    const needEl = h('p', { class: 'atl-apply-need muted' }, t('atl.apply.needTests'));
    const advanced = h('details', { class: 'atl-apply-adv' },
      h('summary', {}, t('atl.apply.advanced')),
      h('div', { class: 'stack' }, field(t('atl.apply.message'), msgInput, { hint: t('atl.apply.messageHint'), optional: true }), force));
    let modal = null;
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => modal?.close('cancel') });
    const confirmBtn = button(t('atl.apply.confirm'), { variant: 'primary', icon: 'send', onClick: () => submit() });
    const sync = () => {
      const allowed = tests?.ok === true || force.input.checked;
      confirmBtn.disabled = !allowed;
      needEl.hidden = allowed;
    };
    async function submit() {
      if (confirmBtn.disabled) return;
      confirmBtn.disabled = true;
      confirmBtn.classList.add('is-busy');
      errBox.hidden = true;
      const body = {};
      if (force.input.checked && tests?.ok !== true) body.force = true;
      const msg = String(msgInput.value ?? '').trim();
      if (msg) body.message = msg;
      try {
        const s = await ctx.api.post(`/api/atelier/sessions/${enc(s0.id)}/apply`, body, { timeoutMs: 300_000 });
        modal?.close('api');
        if (closed) return;
        sessions = upsertSession(sessions, s);
        if (s0.id === currentId) current = s;
        renderAll();
        loadDiff();
        ctx.toast.success(t('atl.apply.done'));
        if (s?.push && !s.push.ok && s.push.error) ctx.toast.warn(s.push.error);
        if (s?.deploy?.started) ctx.toast.info(t('atl.apply.deploy'));
        else if (s?.deploy && !s.deploy.started && s.deploy.error) ctx.toast.warn(s.deploy.error);
      } catch (err) {
        confirmBtn.classList.remove('is-busy');
        sync();
        errBox.textContent = err.message || t('common.error');
        errBox.hidden = false;
        if (!closed) loadCurrent();
      }
    }
    const content = h('div', { class: 'stack atl-apply' },
      h('p', {}, t('atl.apply.intro')),
      h('ol', { class: 'atl-apply-steps' },
        h('li', {}, t('atl.apply.step1', { title: s0.title || '—' })),
        h('li', {}, t('atl.apply.step2', { branch: status?.branch || 'main', remote: status?.remote || 'origin' })),
        h('li', {}, t('atl.apply.step3')),
        h('li', {}, t('atl.apply.step4'))),
      status && status.deployConfigured === false ? h('p', { class: 'atl-alert is-amber' }, icon('alert', { size: 16 }), h('span', {}, t('atl.noDeploy'))) : null,
      testsLine, advanced, needEl, errBox);
    modal = ctx.overlay.openModal({
      title: t('atl.apply.title'), size: 'md', content, footer: [cancel, confirmBtn], className: 'atl-apply-modal', initialFocus: cancel,
    });
    sync();
    return modal;
  }

  // --- live updates -------------------------------------------------------------------------------
  function onEvent(ev) {
    if (closed || !ev || typeof ev !== 'object' || typeof ev.sessionId !== 'string') return;
    sessions = applyEventToList(sessions, ev);
    if (ev.kind === 'state' && status) {
      if (ev.state === 'running') status = { ...status, running: ev.sessionId };
      else if (status.running === ev.sessionId) status = { ...status, running: null };
    }
    if (current && ev.sessionId === current.id) {
      const prevState = current.state;
      current = applyEvent(current, ev);
      if (ev.kind === 'state') {
        scheduleReload();
        const st = current.state;
        if (st !== prevState) {
          conversation.announce(stateInfo(current).label);
          if (prevState === 'running' || st === 'applied') scheduleDiff();
        }
      } else if (ev.kind === 'diff') {
        scheduleDiff();
      }
      renderMain();
    }
    renderSessions();
  }

  function resync() {
    loadStatus();
    loadSessions();
    if (currentId !== null) { loadCurrent(); loadDiff(); }
  }

  const offs = [
    ctx.events.on('atelier', onEvent),
    ctx.events.on('hello', resync),
  ];
  // Fallback when the live connection is down: poll the running session.
  const poll = setInterval(() => {
    if (closed || !current) return;
    const live = current.state === 'running' || current.state === 'testing' || current.busy;
    if (live && ctx.store.get().sse !== 'open') loadCurrent();
  }, POLL_MS);
  // Relative times in the list ("il y a 5 min").
  const clock = setInterval(() => { if (!closed && sessions) renderSessions(); }, 60_000);

  layer.onClose(() => {
    closed = true;
    for (const off of offs) off();
    clearInterval(poll);
    clearInterval(clock);
    clearTimeout(reloadTimer);
    clearTimeout(diffTimer);
  });

  renderIntro();
  renderAll({ full: true });
  loadStatus();
  loadSessions();
  return layer;
}
